import { Injectable } from '@angular/core';
import { LoggerWrapper } from '../logger/logger-wrapper.service';
import { LoggerFactory } from '../logger/logger-factory.service';
import { CapacitorHttp } from '@capacitor/core';
import { PresignedUrlControllerApi } from '../../../api/services/presigned-url-controller-api';
import { firstValueFrom } from 'rxjs';
import { PresignedUrlForDownload$Params } from '../../../api/fn/presigned-url-controller/presigned-url-for-download';
import { GenerateObjectsPresignedUrlsResponse } from '../../../api/models/generate-objects-presigned-urls-response';
import { DocumentContent } from '../download-preview.service';
import { UploadPresignedUrlResponse } from '../../../api/models/upload-presigned-url-response';
import { UploadPresignedUrlDto } from '../../../api/models/upload-presigned-url-dto';

@Injectable({
  providedIn: 'root',
})
export class PresignedUrlService {
  private logger: LoggerWrapper;

  constructor(
    private readonly loggerFactory: LoggerFactory,
    private readonly PresignedUrlApi: PresignedUrlControllerApi
  ) {
    this.logger = this.loggerFactory.getInstance(this);
  }

  async getDocumentsByIds(documentIds: string[]) {
    this.logger.info('Génération des URLs présignées pour la récupération des documents');
    let result = await this.getPresignedUrlsByIds(documentIds);

    this.logger.info('Appels des URLs présignées pour la récupération des documents');
    let documents: any[] = [];
    for (const preSignedUrlDtoResponse of result.preSignedUrlDtoList) {
      let data = await this.downloadDocument(preSignedUrlDtoResponse.presignedUrl);
      documents.push(data.content);
    }
    return documents;
  }

  async getDocumentsById(documentIds: string): Promise<DocumentContent> {
    this.logger.info('Génération des URLs présignées pour la récupération des documents');
    const result = await this.getPresignedUrlsByIds([documentIds]);

    this.logger.info('Appels des URLs présignées pour la récupération des documents');

    const data = await this.downloadDocument(result.preSignedUrlDtoList[0].presignedUrl);

    return { content: data.content };
  }

  async getPresignedUrlsByIds(documentIds: string[]): Promise<GenerateObjectsPresignedUrlsResponse> {
    const ids = documentIds.map((documentId) => {
      return {
        idDocumentArchivage: documentId,
      };
    });

    const params: PresignedUrlForDownload$Params = {
      body: ids,
    };
    let result: GenerateObjectsPresignedUrlsResponse;
    try {
      result = await firstValueFrom(this.PresignedUrlApi.presignedUrlForDownload(params));
      return result;
    } catch (error) {
      this.logger.error(error, `Erreur lors de la génération des urls des documents: ${documentIds}`);
      return Promise.reject(`Erreur lors de la génération des urls des documents: ${documentIds}`);
    }
  }

  async downloadDocument(presignedUrl: string): Promise<DocumentContent> {
    try {
      // Il faut utiliser le CapacitorHttp pour éviter que l'URL présignée soit altérée
      let response = await CapacitorHttp.get({ url: presignedUrl, responseType: 'blob' });
      return {
        content: response.data,
      };
    } catch (error) {
      this.logger.error(error, `Erreur lors du téléchargement du document [${presignedUrl}]`);
      return Promise.reject(`Erreur lors du téléchargement du document [${presignedUrl}]`);
    }
  }

  async getPresignedUrlForUpload(request: UploadPresignedUrlDto): Promise<UploadPresignedUrlResponse> {
    return firstValueFrom(
      this.PresignedUrlApi.presignedUrlForUpload({
        body: request,
      })
    );
  }
}
